<template>
  <div class="field">
    <label v-if="label" class="label has-text-weight-bold" :for="controlId">
      {{ label }}
    </label>
    <div class="control">
      <div
        :class="{
          expanded: expanded,
          'is-readonly': disabled,
          select: !disabled,
        }"
        ref="dropdown"
        style="display: block"
        :aria-expanded="expanded.toString()"
        >
        <popper
          v-if="!disabled"
          ref="esSelect"
          trigger="click"
          :disabled="disabled"
          :options="{
            placement: 'bottom-start',
            positionFixed: true,
            modifiers: {
              preventOverflow: {
                boundariesElement: 'viewport'
              }
            }
          }"
        >
          <div class="popper" ref="popper">
            <div v-if="search" style="margin: 0.5rem 0.5rem 0">
              <div class="multi-search">
                <div class="is-flex wrap">
                  <div
                    class="tag is-flex"
                    v-for="val in values"
                    :key="val.keyValue"
                    style="height: fit-content; align-items: center"
                  >
                    <span v-html="selectedValue(val)" style="padding-right: 0.5rem"></span>
                    <a tabindex="0" role="button"
                      @click.stop="removeValue(val)"
                      @keydown.enter.exact="removeValue(val)"
                      @keydown.space.exact="removeValue(val)"
                    >
                      <font-awesome-icon :icon="['fal', 'times']" title="Remove Value" />
                    </a>
                  </div>
                </div>
                <div class="control has-icons-right">
                  <input
                    :readonly="!allowUserInput"
                    class="input multi-search-bar"
                    type="text"
                    v-model="searchName"
                    style="margin: 0 0.5rem"
                    @keyup="checkLength"
                    aria-label="Search"
                    tabindex="0"
                  />
                  <span
                    v-if="allowUserInput"
                    class="icon icon-input is-small is-right"
                    :class="{ 'cursor-text': !searchName.length }"
                    @click="inputIconAction"
                  >
                    <font-awesome-icon
                      v-if="!searchName.length"
                      :icon="['fal', 'search']"
                      style="color: #999"
                      title="Search"
                    />
                    <font-awesome-icon v-else :icon="['fal', 'times']" title="Clear Search"/>
                  </span>
                </div>
              </div>
              <p v-if="showSearchHelper" class="has-text-grey is-italic"
                style="font-size: 12px; padding-top: 0.5rem"
              >
                Please type in at least {{ searchMinLength }} characters to start search.
              </p>
              <p v-if="!results.length && !loading"
                class="has-text-grey is-italic"
                style="font-size: 12px; padding-top: 0.5rem"
              >
                No results were found. Try changing search criteria.
              </p>
            </div>
            <div
              class="is-flex flex-align--center flex-justify--center"
              v-if="loading" style="min-height: 100px;"
            >
              <es-loader size="3x"/>
            </div>
            <es-exception
              v-else-if="loadingError"
              class="has-text-centered"
              margin="2rem"
              exceptionMessage="There was an issue."
            />
            <div v-else-if="results.length">
              <ul class="menu">
                <es-select-row
                  v-for="(result, i) in results"
                  :key="i"
                  :isHovered="i === arrowCounter"
                  :isActive="isChecked(result)"
                  :multi="multi"
                  :result="result"
                  :subValueTplFn="getSubValue(result)"
                  :value="selectedValue(result)"
                  @onClick="selectValue(result)"
                  @valueSelected="selectValue(result)"
                />
              </ul>
              <div v-if="search && allowUserInput"
                class="is-flex flex-direction--column flex-align--center is-size-7"
                style="padding: 0 0.5rem 0.5rem;"
              >
                <div class="has-text-weight-bold">
                  <span v-if="numberRecords">
                    Display {{ results.length }} of {{ numberRecords }} records.
                  </span>
                  <span v-else>
                    Display {{ results.length }} records.
                  </span>
                </div>
                <div>
                  Search to find additional locations.
                </div>
              </div>
            </div>
            <div
              v-if="search || clearOut"
              class="dropdown-clear"
            >
              <a tabindex="0" role="button"
                class="dropdown-clear--text has-text-primary"
                @click="clear"
                @keypress.enter="clear"
              >
                Clear
              </a>
            </div>
          </div>
          <div
            class="input display-value"
            :class="{ 'is-danger': hasError }"
            readonly
            @click="toggle"
            slot="reference"
            ref="trigger"
            tabindex="0"
            v-html="displayValue"
            @keydown.enter.exact="handleEnterSpaceKeydown"
            @keydown.space.exact.prevent="handleEnterSpaceKeydown"
            :id="controlId"
          ></div>
        </popper>
        <div
          v-else
          class="input display-value is-static is-readonly"
          :class="{'is-readonly-no-bg': displayValue!==null}"
          v-html="displayValue"
        >
        </div>
      </div>
    </div>
    <p v-if="hasError" class="help is-danger">
      {{ errorMsg }}
    </p>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSearch,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import popper from 'vue-popperjs';
import 'vue-popperjs/dist/vue-popper.css';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelectRow from '@/components/EsSelectRow.vue';
import FocusTrapEscape from '../mixins/FocusTrapEscape';

library.add(
  faSearch,
  faTimes,
);

const UPDATE_VALUES = 'update:values';

export default {
  props: {
    /**
     * To allow the user to type in values for search
     */
    allowUserInput: {
      type: Boolean,
      default: true,
    },
    /**
     * The default api used to fetch the dropdown data.
     * Do not pass in an api and setValues.
     */
    api: String,
    /**
    * Pass in aditional query params for the api.
    */
    apiQueryParams: Object,
    /**
     * A boolean value to show the clear in the dropdown.
     */
    clearOut: Boolean,
    /**
    * Help text to display under select.
    */
    errorMsg: String,
    /**
    * A boolean value to make border red.
    */
    hasError: Boolean,
    /**
     * The key for the component.
     * Pass in a key that is unique to it's own object.
     */
    keyValue: String,
    /**
     * The label is displayed above the input box.
     */
    label: String,
    /**
     * A boolean value to show checkboxes.
     */
    multi: Boolean,
    /**
       Get rid of. Pass in :values.sync instead

     * Pass in pre-populated prop if there is an array of pre-existing values.
     * May be used when you cannot pass in preSelected because it does not
     * conform to the standard api/{id} use case.
     */
    prePopulated: Array,
    /**
     * Pass in prep-selected if there is an array of items that use the $(api)
     * to fetch the results.
     * Take an array of ids usually from the url.
     */
    preSelected: [Array, Promise],
    /**
     * A boolean value to show the search bar in the dropdown.
     */
    search: Boolean,
    /**
     * The minimum  character length for search to be called.
     */
    searchMinLength: String,
    /**
     * A pre-defined list of items in the dropdown.
     * Do not pass in setValues and an api.
     */
    setValues: Array,
    /**
     * Subtext that will be displayed under the main text in the dropdown row.
     */
    subValueTplFn: Function,
    /**
     * The default value for what to display in the dropdown and input box.
     * Examples include: {location} or {location.name}
     */
    valueTpl: String,
    /**
     * A call back function that can inject html in the dropdown and input bar.
     * valueTplFn will take a higher priority than valueTpl.
     */
    valueTplFn: Function,
    /**
     * A boolean value to disable
     */
    disabled: Boolean,
    /**
     * The ID on the control to link to the label for accessibility
     */
    controlId: String,
  },
  components: {
    EsException,
    EsLoader,
    EsSelectRow,
    popper,
  },
  data() {
    return {
      arrowCounter: -1,
      expanded: false,
      hasExpanded: false,
      hasSearchName: false,
      loading: false,
      loadingError: null,
      numberRecords: null,
      /**
       * The dropdown items in the list are stored in the results array.
       */
      results: [],
      searchName: '',
      timeout: null,
      values: [],
    };
  },
  mixins: [FocusTrapEscape],
  watch: {
    /**
     * Resets the dropdown when an outside query param comes in.
     * hasExpanded so that it will fetch the new apiQueryParams.
     * searchName so that the pre-existing searchName is not there.
     * Clears any selected values from before params are sent in.
     */
    apiQueryParams() {
      this.hasExpanded = false;
      if (this.searchName) {
        this.searchName = '';
      }
      if (this.values.length) {
        this.values.splice(0, this.values.length);
        this.$emit(UPDATE_VALUES, this.values);
      }
    },
    hasExpanded() {
      if (this.api) {
        this.callApi(this.api);
      }
    },
    expanded() {
      this.$nextTick(() => {
        if (this.expanded) {
          this.focusFirstSelectedValue();
          document.addEventListener('mousedown', this.handleClickOutside);
          document.addEventListener('keydown', this.handleKeydownEvents);
        } else {
          this.arrowCounter = -1;
          document.removeEventListener('mousedown', this.handleClickOutside);
          document.removeEventListener('keydown', this.handleKeydownEvents);
        }
      });
    },
    setValues() {
      this.results = this.setValues;
    },
  },
  async created() {
    if (this.preSelected) {
      await this.getPreSelected();
    }
  },
  mounted() {
    if (this.setValues) {
      this.results = this.setValues;
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydownEvents);
  },
  methods: {
    getPopperFocusableElements() {
      const popperEl = this.$refs.popper;
      if (!popperEl) return [];
      return Array.from(
        popperEl.querySelectorAll(
          '[tabindex]:not([tabindex="-1"])')
      ).filter(el => !el.disabled && !el.hidden);
    },
    focusTrigger() {
      this.$refs.trigger.focus({ preventScroll: true });
    },
    handleKeydownEvents(evt) {
      if (!this.expanded) return;
      if (evt.key === 'Escape') {
        this.close();
        this.focusTrigger();
      }
      if (evt.key === 'Tab' || evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
        this.handleKeyNavigation(evt);
      }
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.expanded = false;
      }
    },
    async callApi(api) {
      try {
        this.toggleLoading();
        let res = '';
        if (this.apiQueryParams) {
          res = await axios.get(`${this.api}`,
            {
              params: this.apiQueryParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            });
        } else {
          res = await axios.get(api);
        }

        if (res.data.data) {
          this.results = res.data.data;
          this.numberRecords = res.data.totalCount;
        } else {
          this.results = res.data;
        }
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    inputIconAction() {
      if (this.searchName.length) {
        this.searchName = '';
        this.searchQuery();
      }
    },
    async getPreSelected() {
      const selected = await Promise.resolve(this.preSelected);

      if (selected.length) {
        if (selected[0] instanceof Object) {
          this.values.push(...selected);
          return;
        }
      }
      if (!this.setValues) {
        /**
         * This format may cover most case:
         * api/{id}
         */
        const promises = [];
        for (let i = 0; i < this.preSelected.length; i += 1) {
          let obj;
          if (this.apiQueryParams) {
            obj = {
              params: this.apiQueryParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            };
          }
          promises.push(
            axios.get(`${this.api}/${this.preSelected[i]}`, obj),
          );
        }
        const responses = await Promise.all(promises);
        this.values.push(...responses.map(res => res.data));
      } else {
        this.values = this.results.filter((item) => {
          if (this.preSelected.includes(item[this.keyValue])) {
            return true;
          }
          return false;
        });
      }
    },
    async searchQuery() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(async () => {
        try {
          const tempParams = {};
          this.loading = true;
          this.loadingError = false;

          if (this.apiQueryParams) {
            Object.assign(tempParams, this.apiQueryParams);
          }

          if (this.searchName.length >= this.searchMinLength) {
            tempParams.q = this.searchName;
            this.hasSearchName = true;
          }

          const res = await axios.get(`${this.api}`,
            {
              params: tempParams,
              paramsSerializer(params) {
                return qs.stringify(params, { arrayFormat: 'repeat' });
              },
            });

          if (res.data.data) {
            this.results = res.data.data;
          } else {
            this.results = res.data;
          }
          this.numberRecords = res.data.totalCount;
        } catch (err) {
          this.loadingError = true;
        } finally {
          this.loading = false;
        }
      }, 500);
    },
    applyTpl(tpl, source) {
      let result = source;
      tpl.replaceAll(/\${((\S(\.)*)+)}/g, (k, v) => {
        const levels = v.split('.');
        levels.forEach((l) => { result = result[l]; });
      });
      return result;
    },
    checkLength() {
      if (this.searchName.length >= this.searchMinLength) {
        this.searchQuery();
      } else if (this.hasSearchName) {
        this.hasSearchName = false;
        this.searchQuery();
      }
    },
    clear() {
      if (this.searchName) {
        this.searchName = '';
        this.searchQuery();
      }
      this.values.splice(0);
      this.$emit(UPDATE_VALUES, this.values);
    },
    isChecked(res) {
      if (this.values.filter(val => val[this.keyValue] === res[this.keyValue]).length > 0) {
        return true;
      }
      return false;
    },
    onArrowUpDown(event) {
      if (!this.results || this.results.length === 0) {
        return;
      }
      event.preventDefault(); // prevent page scrolling
      if (event.key === 'ArrowDown') {
        this.arrowCounter = (this.arrowCounter + 1) % this.results.length;
      } else if (event.key === 'ArrowUp') {
        this.arrowCounter = (this.arrowCounter - 1 + this.results.length) % this.results.length;
      }
    },
    onHover(res) {
      this.arrowCounter = this.results.findIndex(
        result => result[this.keyValue] === res[this.keyValue],
      );
    },
    /**
     * use pushValues() when using $refs to reset the values. Like resetting the years from filter.
     */
    pushValues(vals) {
      this.values.push(...vals);
    },
    removeValue(val) {
      this.values.splice(this.values.findIndex(
        value => value[this.keyValue] === val[this.keyValue],
      ), 1);
      this.$emit(UPDATE_VALUES, this.values);
    },
    rowIsActive(index) {
      if (!this.multi && (index === this.arrowCounter)) {
        return true;
      }
      return false;
    },
    toggle() {
      this.expanded = !this.expanded;
      if (!this.hasExpanded) {
        this.hasExpanded = true;
      }
    },
    handleKeyNavigation(event) {
      const focusables = this.getPopperFocusableElements();
      const currentIndex = focusables.indexOf(document.activeElement);
      const last = focusables[focusables.length - 1];
      const first = focusables[0];
      const prevIndex = currentIndex === 0 ? 0 : currentIndex - 1;
      const nextIndex = currentIndex === focusables.length - 1 ? 0 : currentIndex + 1;

      if (this.expanded) {
        if (event.key === 'Tab' && event.shiftKey) {
          if (document.activeElement === first || document.activeElement === this.$refs.trigger) {
            event.preventDefault();
            this.close();
            this.focusPrevOutside(document.activeElement === first ? 'trigger' : 'popper');
            return;
          }
          this.navigatePopper(event, focusables, prevIndex, true);
        } else if (event.key === 'Tab') {
          if (document.activeElement === last) {
            this.close();
            this.focusNextOutside('trigger');
            return;
          }
          this.navigatePopper(event, focusables, nextIndex, false);
        }

        if ((event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
          this.onArrowUpDown(event);
          this.focusRow();
        }
      }
    },
    navigatePopper(event, focusables, idx, isBackwards) {
      event.preventDefault();
      if (focusables[idx].tagName === 'LI') {
        if (isBackwards) {
          this.arrowCounter = this.arrowCounter === -1 ? this.results.length - 1 : (this.arrowCounter - 1 + this.results.length) % this.results.length;
        } else {
          this.arrowCounter = (this.arrowCounter + 1) % this.results.length;
        }
        this.focusRow();
      } else {
        this.arrowCounter = -1;
        focusables[idx].focus({ preventScroll: true });
        this.scrollToSelected(focusables[idx]);
      }
    },
    focusRow() {
      const focusables = this.getPopperFocusableElements().filter(elem => elem.tagName === 'LI');
      if (!focusables.length) {
        return;
      }
      focusables[this.arrowCounter].focus({ preventScroll: true });
      this.scrollToSelected(focusables[this.arrowCounter]);
    },
    focusFirstSelectedValue() {
      const firstSelectedValue = this.values[0];
      if (firstSelectedValue) {
        this.arrowCounter = this.results.findIndex(res => JSON.stringify(res) === JSON.stringify(firstSelectedValue));
      }
      if (this.arrowCounter >= 0) {
        this.focusRow();
      }
    },
    scrollToSelected(el) {
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    },
    close() {
      this.expanded = false;
      this.hasExpanded = false;
      this.$refs.esSelect.showPopper = false;
    },
    toggleLoading() {
      this.loading = !this.loading;
    },
    handleEnterSpaceKeydown() {
      if (this.arrowCounter < 0) {
        this.togglePopper();
      }
    },
    togglePopper() {
      this.toggle();
      this.$refs.esSelect.showPopper = !this.$refs.esSelect.showPopper;
    },
    selectValue(val) {
      if (this.multi) {
        // removing a value
        if (this.values.filter(
          value => value[this.keyValue] === val[this.keyValue],
        ).length > 0) {
          this.values.splice(this.values.findIndex(
            value => value[this.keyValue] === val[this.keyValue],
          ), 1);
        } else { // adding a value
          this.values.push(val);
        }
      } else {
        // replace single value with selected
        this.values.splice(0, this.values.length, this.results.find(
          result => result[this.keyValue] === val[this.keyValue],
        ));
        this.togglePopper();
        this.focusTrigger();
      }
      this.$emit(UPDATE_VALUES, this.values);
    },
    selectedValue(val) {
      if (!this.valueTplFn) {
        return this.applyTpl(this.valueTpl, val);
      }
      return this.valueTplFn.call(this, val);
    },
    getSubValue(val) {
      if (this.subValueTplFn) {
        return this.subValueTplFn.call(this, val);
      }
      return null;
    },
  },
  computed: {
    displayValue() {
      let value;
      if (this.values.length > 1) {
        value = 'Multiple';
      } else if (this.values.length === 1) {
        if (this.valueTplFn) {
          value = this.valueTplFn.call(this, this.values[0]);
        } else {
          value = this.applyTpl(this.valueTpl, this.values[0]);
        }
        value = value.replace(/(\(\d+\))$/, '&nbsp;<i style="color: #737373; font-size: 0.75rem">$1</i>');
        return `<span>${value}</span>`;
      } else {
        value = null;
      }
      return value;
    },
    showSearchHelper() {
      if (this.searchName.length >= 1 && this.searchName.length < this.searchMinLength) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style lang="sass" scoped>
  .errored
    border-color: red
  .input
    padding-right: 2.5rem
    overflow: hidden
    white-space: nowrap
    text-overflow: ellipsis
    &:hover
    cursor: pointer
  .icon-input
    height: 100% !important
    color: #999
  .cursor-text
    cursor: text
  .select
    width: -webkit-fill-available
    min-height: 2rem !important
    height: auto
  .select.expanded
    &::after
      transform: rotate(135deg) !important
      -webkit-transform: rotate(135deg) !important
      margin-top: -0.1em !important
  .dropdown-clear
    margin: 0 0.5rem
    padding: 0.5rem 0
    border-top: 1px solid #e9ecef
  .dropdown-clear--text
    padding: 0
  .popper
    background-color: #f4f4f4 !important
    -webkit-box-shadow: none !important
    box-shadow: 0 4px 9px -4px #555 !important
    padding: 8px 0px !important
    text-align: left
    padding: 0 !important
    .popper__arrow
      top: 0px
  .menu
    padding: .5rem
    list-style: none
    margin: 0
    max-height: 250px
    overflow: auto
  .multi-search
    border: 1px solid #ced4da
    border-radius: .25rem
    background-color: #fff
  .multi-search-bar
    width: -webkit-fill-available
    border: none !important
    cursor: text
    &:focus
      outline: none
  .tag
    margin: 0.25rem
    align-items: flex-start
    font-weight: normal
    background-color: #ddd
  .is-readonly
    padding-left: 0.3rem
    cursor: not-allowed
    outline: none
    pointer-events: none
    user-select: none
  .is-readonly:focus
    outline: none
  .input
    min-height: 25px
    height: auto
    white-space: normal
  .input.is-readonly
    background: #eee
  .input.is-readonly-no-bg
    background: none
  .display-value
    overflow-wrap: break-word
    white-space: normal
    word-break: break-all
</style>
